import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { StripeConnectAccountState } from '../types/stripe-connect';
import { StripeConnectApiService } from './api/stripe-connect.service';
import { ToastService } from './toast.service';
import { TranslateConfigService } from './translate-config.service';
import { UserStateService } from './user-state.service';
import { AppStorageService } from './app-storage.service';
import { StorageKeys } from './storage-keys';

const REFRESH_WINDOW_MS = 60 * 1000; // 1 minute cache window

@Injectable({ providedIn: 'root' })
export class StripeConnectStateService {
  private readonly stateSubject = new BehaviorSubject<StripeConnectAccountState>(this.readStateFromStorage());
  public readonly state$ = this.stateSubject.asObservable();

  private lastRefreshTs: number | null = null;
  private onboardingRequestInFlight = false;

  constructor(
    private readonly api: StripeConnectApiService,
    private readonly toastService: ToastService,
    private readonly translate: TranslateConfigService,
    private readonly alertController: AlertController,
    private readonly userStateService: UserStateService,
    private readonly storage: AppStorageService,
  ) {
    this.userStateService.user$.subscribe((user) => {
      if (!user || !Object.keys(user).length) {
        return;
      }
      const state = this.buildStateFromUser(user);
      if (state) {
        this.mergeState(state, false);
      }
    });
  }

  get snapshot(): StripeConnectAccountState {
    return this.stateSubject.value;
  }

  isOnboarded(state: StripeConnectAccountState = this.snapshot): boolean {
    return (
      !!state?.status &&
      state.status === 'verified' &&
      !!state.charges_enabled &&
      !!state.payouts_enabled
    );
  }

  async requireOnboarded(context: string = 'default', options: { showPrompt?: boolean; forceRefresh?: boolean } = {}): Promise<boolean> {
    const state = await this.ensureFreshState(options.forceRefresh);
    if (this.isOnboarded(state)) {
      return true;
    }

    if (options.showPrompt !== false) {
      await this.presentOnboardingAlert(context);
    } else {
      const message = this.translate.getItemTranslated('ACCOUNT.STRIPE_CONNECT.PROMPT_MESSAGE') || '';
      this.toastService.showWarn('', message);
    }

    return false;
  }

  async refreshStatus(force = false): Promise<StripeConnectAccountState> {
    const now = Date.now();
    const shouldFetch =
      force ||
      !this.lastRefreshTs ||
      now - this.lastRefreshTs >= REFRESH_WINDOW_MS;

    if (!shouldFetch) {
      return this.snapshot;
    }

    try {
      const state = await firstValueFrom(this.api.status());
      this.lastRefreshTs = now;
      const enhanced = { ...state, last_synced_at: new Date().toISOString() };
      this.mergeState(enhanced);
      this.persistState(enhanced);
      this.updateUserStripeFields(enhanced);
      return enhanced;
    } catch {
      return this.snapshot;
    }
  }

  async startOnboardingFlow(businessType?: string): Promise<boolean> {
    if (this.onboardingRequestInFlight) {
      return false;
    }
    this.onboardingRequestInFlight = true;

    // En web abrimos la pestaña de forma SÍNCRONA dentro del gesto del usuario.
    // El navegador bloquea los popups (window.open) que se abren después de un
    // await; al pedir primero el link al backend y abrir la pestaña después, el
    // popup quedaba bloqueado y se mostraba "Error al conectar con Stripe".
    let preopenedWindow: Window | null = null;
    if (!Capacitor.isNativePlatform() && typeof window !== 'undefined') {
      preopenedWindow = window.open('', '_blank');
    }

    try {
      const link = await firstValueFrom(this.api.onboard(businessType));
      if (!link?.url) {
        preopenedWindow?.close();
        throw new Error('missing_url');
      }
      if (preopenedWindow) {
        preopenedWindow.location.href = link.url;
      } else {
        await this.openExternalLink(link.url);
      }
      return true;
    } catch (error) {
      preopenedWindow?.close();
      const message = this.translate.getItemTranslated('ACCOUNT.STRIPE_CONNECT.ONBOARDING_ERROR') || 'Unable to open Stripe Connect';
      this.toastService.showError('', message);
      return false;
    } finally {
      this.onboardingRequestInFlight = false;
    }
  }

  private async ensureFreshState(force = false): Promise<StripeConnectAccountState> {
    if (force || !this.lastRefreshTs) {
      return this.refreshStatus(true);
    }

    if (Date.now() - this.lastRefreshTs > REFRESH_WINDOW_MS) {
      return this.refreshStatus(true);
    }

    return this.snapshot;
  }

  private mergeState(next: Partial<StripeConnectAccountState>, emit = true): StripeConnectAccountState {
    const current = this.snapshot;
    const merged: StripeConnectAccountState = {
      status: next.status ?? current.status ?? null,
      charges_enabled: next.charges_enabled ?? current.charges_enabled ?? false,
      payouts_enabled: next.payouts_enabled ?? current.payouts_enabled ?? false,
      requirements_due: next.requirements_due ?? current.requirements_due ?? [],
      onboarded_at: next.onboarded_at ?? current.onboarded_at ?? null,
      last_synced_at: next.last_synced_at ?? current.last_synced_at ?? null,
    };

    if (emit) {
      this.stateSubject.next(merged);
    }

    return merged;
  }

  private buildStateFromUser(user: any): StripeConnectAccountState | null {
    if (!user) {
      return null;
    }

    if (
      typeof user.stripe_connect_status === 'undefined' &&
      typeof user.stripe_connect_charges_enabled === 'undefined'
    ) {
      return null;
    }

    return {
      status: (user.stripe_connect_status as StripeConnectAccountState['status']) ?? null,
      charges_enabled: Boolean(user.stripe_connect_charges_enabled),
      payouts_enabled: Boolean(user.stripe_connect_payouts_enabled),
      requirements_due: Array.isArray(user.stripe_connect_requirements_due)
        ? user.stripe_connect_requirements_due
        : [],
      onboarded_at: user.stripe_connect_onboarded_at ?? null,
      last_synced_at: null,
    };
  }

  private readStateFromStorage(): StripeConnectAccountState {
    try {
      const cached = this.storage.getJSON<StripeConnectAccountState>(StorageKeys.STRIPE_CONNECT_STATE);
      if (cached) {
        return cached;
      }
    } catch (error) {
    }

    return {
      status: null,
      charges_enabled: false,
      payouts_enabled: false,
      requirements_due: [],
      onboarded_at: null,
      last_synced_at: null,
    };
  }

  private persistState(state: StripeConnectAccountState): void {
    try {
      this.storage.setJSON(StorageKeys.STRIPE_CONNECT_STATE, state);
    } catch (error) {
    }
  }

  private updateUserStripeFields(state: StripeConnectAccountState): void {
    try {
      const user = this.storage.getJSON<any>(StorageKeys.USER);
      if (!user) {
        return;
      }
      user.stripe_connect_status = state.status;
      user.stripe_connect_charges_enabled = state.charges_enabled;
      user.stripe_connect_payouts_enabled = state.payouts_enabled;
      user.stripe_connect_requirements_due = state.requirements_due ?? [];
      user.stripe_connect_onboarded_at = state.onboarded_at;
      this.storage.setJSON(StorageKeys.USER, user);
      this.userStateService.updateUser(user);
    } catch (error) {
    }
  }

  private async presentOnboardingAlert(context: string): Promise<void> {
    const header = this.translate.getItemTranslated('ACCOUNT.STRIPE_CONNECT.PROMPT_TITLE') || 'Conecta tu cuenta';
    const message = this.translate.getItemTranslated('ACCOUNT.STRIPE_CONNECT.PROMPT_MESSAGE') || '';
    const action = this.translate.getItemTranslated('ACCOUNT.STRIPE_CONNECT.PROMPT_ACTION') || 'Ir a Stripe';
    const later = this.translate.getItemTranslated('ACCOUNT.STRIPE_CONNECT.PROMPT_LATER') || 'Más tarde';

    const alert = await this.alertController.create({
      header,
      message,
      buttons: [
        {
          text: later,
          role: 'cancel',
        },
        {
          text: action,
          handler: () => this.startOnboardingFlow(),
        },
      ],
    });

    await alert.present();
  }

  private async openExternalLink(url: string): Promise<void> {
    if (!url) {
      return;
    }

    try {
      if (Capacitor.isPluginAvailable('Browser')) {
        await Browser.open({ url, presentationStyle: 'fullscreen' });
        return;
      }
    } catch (error) {
    }

    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener');
    }
  }
}
